/**
 * Analytics of the storage preview pane. Enums and buckets only — never a container, blob, path or file name.
 * `feature:storage_preview_shown` fires once per content kind per pane opening, not per file, so arrowing through a
 * folder does not flood the event stream.
 */
import type { StorageExplorerMode } from '../../core/types/workspaceTypes';
import type { JsonEditChangeKind } from '../../common/components/jsonTreeEdit';
import { trackStorageEvent } from '../services/storageAnalytics';

export type PreviewShownKind = 'json' | 'markdown' | 'text' | 'svg' | 'image' | 'video' | 'audio' | 'pdf' | 'binary' | 'too_large' | 'archived';
export type PreviewSaveMode = 'overwrite' | 'overwrite_any' | 'copy';
export type PreviewSaveOutcome = 'saved' | 'conflict' | 'exists' | 'error';

const shownThisOpening = new Set<string>();

export function trackPreviewToggle(enabled: boolean, mode: StorageExplorerMode): void {
  if (enabled) shownThisOpening.clear();
  trackStorageEvent('toolbar:storage_preview', { enabled, mode });
}

export function trackPreviewShown(kind: PreviewShownKind, mode: StorageExplorerMode): void {
  const key = `${mode}:${kind}`;
  if (shownThisOpening.has(key)) return;
  shownThisOpening.add(key);
  trackStorageEvent('feature:storage_preview_shown', { kind, mode });
}

export function trackPreviewView(kind: 'json' | 'markdown' | 'svg' | 'image' | 'text', view: string): void {
  trackStorageEvent('feature:storage_preview_view', { kind, view });
}

export function trackPreviewEdit(action: 'start' | 'cancel' | 'discard', kind: string, mode: StorageExplorerMode): void {
  trackStorageEvent('feature:storage_preview_edit', { action, kind, mode });
}

export function trackPreviewSave(saveMode: PreviewSaveMode, outcome: PreviewSaveOutcome, mode: StorageExplorerMode, target?: 'same' | 'other'): void {
  trackStorageEvent('feature:storage_preview_save', { mode: saveMode, outcome, service: mode, ...(target ? { target } : {}) });
}

export function trackPreviewLoadAll(mode: StorageExplorerMode): void {
  trackStorageEvent('feature:storage_preview_load_all', { mode });
}

/** "Show as text" on content that looked binary, and Retry after a failed read. */
export function trackPreviewRecovery(action: 'show_as_text' | 'retry', mode: StorageExplorerMode): void {
  trackStorageEvent(action === 'retry' ? 'feature:storage_preview_retry' : 'feature:storage_preview_show_as_text', { mode });
}

/** The Save a copy dialog opened (from the edit bar or the conflict notice) or dismissed without a copy. */
export function trackPreviewCopyDialog(action: 'open' | 'cancel', mode: StorageExplorerMode, from?: 'edit_bar' | 'conflict'): void {
  trackStorageEvent('feature:storage_preview_copy_dialog', { action, mode, ...(from ? { from } : {}) });
}

/** An edit made in the JSON tree while editing a file (the change kind only: leaf, add / remove key or element). */
export function trackPreviewTreeEdit(kind: JsonEditChangeKind): void {
  trackStorageEvent('feature:storage_preview_tree_edit', { change_kind: kind });
}
